import { useState, useEffect, useCallback } from 'react';
import { RefreshCw, X } from 'lucide-react';

const API_BASE = (import.meta.env.VITE_API_URL || 'http://localhost:3005') + '/api';

interface LayerRow {
  month: string; recurring: number; recurring_payers: number;
  l4: number; l4_buyers: number; l5: number; l5_buyers: number;
}
interface SubRow { status: string; subs: number; }
interface DetailRow { layer: string; plan: string; billing: string; payers: number; txns: number; revenue: number; }

const fmt = (v: number) => '$' + Math.abs(v).toLocaleString();
const monthLabel = (d: string) => new Date(d).toLocaleString('en', { month: 'short', year: '2-digit', timeZone: 'UTC' });

export default function MembershipLayers() {
  const [since, setSince] = useState('2025-11-01');
  const [until, setUntil] = useState('');
  const [layers, setLayers] = useState<LayerRow[]>([]);
  const [subs, setSubs] = useState<SubRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<LayerRow | null>(null);
  const [detail, setDetail] = useState<DetailRow[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);

  const openDetail = async (row: LayerRow) => {
    setSelected(row); setDetail([]); setDetailLoading(true);
    try {
      const month = String(row.month).slice(0, 10);
      const r = await fetch(`${API_BASE}/financial-reports/membership-layers/detail?month=${month}`);
      const j = await r.json();
      if (j.success) setDetail(j.data || []);
    } catch { /* empty state */ }
    setDetailLoading(false);
  };

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await fetch(`${API_BASE}/financial-reports/membership-layers?since=${since}${until ? `&until=${until}` : ''}`);
      const j = await r.json();
      if (!j.success) throw new Error(j.error);
      setLayers(j.data.layers || []); setSubs(j.data.subs || []);
    } catch (e: any) { setError(e.message); }
    setLoading(false);
  }, [since, until]);

  useEffect(() => { load(); }, [load]);

  const tot = layers.reduce((a, r) => ({
    recurring: a.recurring + Number(r.recurring), l4: a.l4 + Number(r.l4), l5: a.l5 + Number(r.l5),
  }), { recurring: 0, l4: 0, l5: 0 });
  const active = subs.find(s => s.status === 'active')?.subs || 0;
  const trials = subs.find(s => s.status === 'in_trial')?.subs || 0;
  const cancelled = subs.find(s => s.status === 'cancelled')?.subs || 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Membership Layers</h1>
          <p className="text-sm text-gray-500">Recurring (Chargebee) vs Level 4 programs vs Level 5 — the three membership businesses, separated.</p>
        </div>
        <div className="flex items-center gap-2">
          <input type="date" value={since} onChange={e => setSince(e.target.value)} className="border rounded px-2 py-1 text-sm" />
          <span className="text-gray-400">→</span>
          <input type="date" value={until} onChange={e => setUntil(e.target.value)} className="border rounded px-2 py-1 text-sm" placeholder="today" />
          <button onClick={load} className="flex items-center gap-1 bg-blue-600 text-white px-3 py-1.5 rounded text-sm hover:bg-blue-700">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      {error && <div className="bg-red-50 text-red-700 p-3 rounded">{error}</div>}

      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        {[
          ['Recurring collected', fmt(tot.recurring)],
          ['Level 4 programs', fmt(tot.l4)],
          ['Level 5 programs', fmt(tot.l5)],
          ['Active subs', active.toLocaleString()],
          ['In trial', trials.toLocaleString()],
          ['Cancelled (reactivation pool)', cancelled.toLocaleString()],
        ].map(([label, v]) => (
          <div key={label as string} className="bg-white rounded-lg shadow p-4">
            <div className="text-xs uppercase text-gray-500">{label}</div>
            <div className="text-xl font-bold text-gray-900">{v}</div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              {['Month', 'Recurring', 'Payers', 'Level 4', 'L4 buyers', 'Level 5', 'L5 buyers', 'Total'].map(h => (
                <th key={h} className="px-3 py-2 text-right first:text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {layers.map(r => (
              <tr key={r.month} onClick={() => openDetail(r)} className="cursor-pointer hover:bg-blue-50">
                <td className="px-3 py-2 font-medium">{monthLabel(r.month)}</td>
                <td className="px-3 py-2 text-right">{fmt(Number(r.recurring))}</td>
                <td className="px-3 py-2 text-right text-gray-500">{r.recurring_payers}</td>
                <td className="px-3 py-2 text-right">{fmt(Number(r.l4))}</td>
                <td className="px-3 py-2 text-right text-gray-500">{r.l4_buyers}</td>
                <td className="px-3 py-2 text-right">{fmt(Number(r.l5))}</td>
                <td className="px-3 py-2 text-right text-gray-500">{r.l5_buyers}</td>
                <td className="px-3 py-2 text-right font-bold">{fmt(Number(r.recurring) + Number(r.l4) + Number(r.l5))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {selected && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between p-4 border-b">
              <div>
                <h2 className="text-lg font-bold text-gray-900">{monthLabel(selected.month)} — plan & tier breakdown</h2>
                <p className="text-sm text-gray-500">Recurring by Chargebee plan · programs by product</p>
              </div>
              <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
            </div>
            <div className="overflow-y-auto p-4">
              {detailLoading ? (
                <p className="text-sm text-gray-400 py-8 text-center">Loading…</p>
              ) : detail.length === 0 ? (
                <p className="text-sm text-gray-400 py-8 text-center">No membership activity found.</p>
              ) : (
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-xs text-gray-500 uppercase border-b">
                      <th className="text-left py-2 pr-2">Plan / Product</th>
                      <th className="text-left py-2 px-2">Billing</th>
                      <th className="text-right py-2 px-2">Payers</th>
                      <th className="text-right py-2 px-2">Txns</th>
                      <th className="text-right py-2 pl-2">Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {['Recurring', 'Level 4', 'Level 5'].map(layer => {
                      const rowsFor = detail.filter(d => d.layer === layer);
                      if (rowsFor.length === 0) return null;
                      const sub = rowsFor.reduce((a, d) => a + Number(d.revenue), 0);
                      return (
                        <>
                          <tr key={layer} className="bg-gray-100">
                            <td colSpan={4} className="py-1.5 px-2 font-semibold text-gray-700">{layer}</td>
                            <td className="py-1.5 pl-2 text-right font-semibold">{fmt(sub)}</td>
                          </tr>
                          {rowsFor.map((d, i) => (
                            <tr key={layer + i} className="border-b border-gray-100 hover:bg-gray-50">
                              <td className="py-1.5 pr-2 pl-4">{d.plan}</td>
                              <td className="py-1.5 px-2 text-gray-500">{d.billing}</td>
                              <td className="text-right py-1.5 px-2 text-gray-500">{d.payers}</td>
                              <td className="text-right py-1.5 px-2 text-gray-500">{d.txns}</td>
                              <td className="text-right py-1.5 pl-2">{fmt(Number(d.revenue))}</td>
                            </tr>
                          ))}
                        </>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            <p className="text-xs text-gray-400 px-4 pb-3">
              Recurring plans join Chargebee transactions to subscriptions; 'One-time / unlinked' = payments without a subscription record.
            </p>
          </div>
        </div>
      )}

      <p className="text-xs text-gray-400">
        Click any month for the plan &amp; tier breakdown. Recurring = successful Chargebee payments (authoritative archive). L4/L5 = Keap program sales from the Wicked order warehouse
        (98% school-linked — forecast from the school calendar). Payers &lt; actives because of annual plans.
      </p>
    </div>
  );
}
